<template>
  <v-container fluid data-testid="dl-page">
    <v-row class="ga-4">
      <v-col>
        <!--
          Primary action row, above the list and aligned right.

          The add control lives HERE rather than in the viewport-pinned FAB
          it replaced. That FAB carried `position="fixed"
          location="bottom end"`, putting it in the same viewport corner as
          App.vue's global `<v-footer app>` (rendered for admin users); the
          footer painted over it and the control was unreachable. A control
          placed above the list also survives the empty-results state,
          where a bottom-anchored one is easiest to lose.

          NO in-page <h1> here, deliberately. The `superadmin` layout's
          Topbar already renders "Distributors" as the page title for this
          route (SA_TITLE_MAP in SuperAdminLayout.vue), so a page-level
          heading would show the same word twice. Several sibling SA pages
          (Users, Money) do duplicate it; that is a pre-existing
          inconsistency and not one to spread here. The action therefore
          sits in a right-aligned row of its own -- same shape the
          distributor Stores page gets from `section-header`, minus the
          redundant title. `section-header` itself is a distributor-app
          component and requires a `title` prop, so reusing it here would
          have forced exactly the duplicate heading we are avoiding.
        -->
        <div class="dl-header">
          <v-btn
            color="primary"
            rounded="pill"
            data-testid="dl-new-distributor"
            @click="$router.push('/admin/distributors/add')"
          >
            <v-icon start color="white" icon="add" />
            <span class="text-white">New Distributor</span>
          </v-btn>
        </div>

        <div class="sa-filter-bar d-flex ga-3 mb-3 align-center">
          <v-text-field
            type="search"
            v-model="search"
            placeholder="Search distributors..."
            data-testid="sa-search"
            density="compact"
            variant="outlined"
            hide-details
            clearable
            prepend-inner-icon="search"
            @update:modelValue="onSearchInput"
          />
          <v-select
            v-model="stateFilter"
            :items="stateOptions"
            item-title="text"
            item-value="value"
            placeholder="All states"
            data-testid="sa-state-filter"
            density="compact"
            variant="outlined"
            hide-details
            clearable
            style="max-width: 200px"
            @update:modelValue="applyFilters"
          />
        </div>

        <v-table class="elevation-1">
          <thead>
            <tr id="table-header">
              <th width="32" />
              <th
                v-for="header in headers"
                :key="header.value"
                :class="[
                  'column sortable',
                  pagination.descending ? 'desc' : 'asc',
                  header.sortable === false ? 'not-sortable' : 'is-sortable',
                  header.value === pagination.sortBy ? 'active' : '',
                ]"
                :style="header.sortable === false ? 'cursor: default' : 'cursor: pointer'"
                @click="sortColumn(header)"
              >
                <div class="d-flex align-center">
                  {{ header.text }}
                  <v-icon
                    v-if="header.sortable !== false"
                    size="small"
                    class="sort-icon"
                    :class="{ 'sort-icon-active': header.value === pagination.sortBy }"
                    :icon="getSortIcon(header)"
                  />
                </div>
              </th>
              <th width="120" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in items"
              :key="item.id"
              :data-testid="`dl-row-${item.id}`"
            >
              <td class="text-center">
                <span
                  class="sa-health-dot"
                  :class="`sa-health-dot--${item.health || 'gray'}`"
                  :data-testid="`dl-health-${item.id}`"
                  :title="`Activity: ${item.health}`"
                />
              </td>
              <td>
                <a
                  href="#"
                  class="text-primary font-weight-medium"
                  :data-testid="`dl-name-${item.id}`"
                  @click.prevent="$router.push('/admin/distributors/' + item.id)"
                >{{ item.name }}</a>
                <span
                  v-if="isDisabled(item)"
                  class="sa-disabled-badge"
                  :data-testid="`dl-disabled-badge-${item.id}`"
                  title="Disabled -- nobody at this distributor can sign in and its stores are offline."
                >Disabled</span>
              </td>
              <td :data-testid="`dl-contact-${item.id}`">
                <a
                  v-if="item.owner"
                  :href="`mailto:${item.owner.email}`"
                  :title="`Send email to ${item.owner.first_name} ${item.owner.last_name} (${item.owner.email})`"
                >{{ item.owner.first_name }} {{ item.owner.last_name }}</a>
                <span v-else class="sa-empty">&mdash;</span>
              </td>
              <td :data-testid="`dl-state-${item.id}`">
                <span
                  v-if="item.state"
                  class="sa-state-pill"
                  :class="`sa-state-pill--${item.state}`"
                >{{ formatState(item.state) }}</span>
                <span v-else class="sa-empty">&mdash;</span>
              </td>
              <td :data-testid="`dl-stores-${item.id}`">{{ item.stores }}</td>
              <td :data-testid="`dl-mrr-${item.id}`">{{ formatMrrCents(item.mrr_cents) }}</td>
              <td :data-testid="`dl-sales-${item.id}`">{{ formatSalesCents(item.sales_30d_cents) }}</td>
              <td :data-testid="`dl-activity-${item.id}`">
                <span v-if="item.last_activity_at">{{ formatDate(item.last_activity_at) }}</span>
                <span v-else class="sa-empty">&mdash;</span>
              </td>
              <td class="dl-row-actions">
                <v-btn
                  size="small"
                  variant="outlined"
                  color="primary"
                  rounded="pill"
                  :data-testid="`dl-open-${item.id}`"
                  @click.stop="doImpersonate(item)"
                >
                  Impersonate
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  :color="isDisabled(item) ? 'primary' : 'error'"
                  :data-testid="`dl-disable-${item.id}`"
                  :disabled="pendingAccessId === item.id"
                  @click.stop="onAccessAction(item)"
                >
                  {{ isDisabled(item) ? "Enable" : "Disable" }}
                </v-btn>
              </td>
            </tr>
            <tr v-if="items.length === 0 && !itemsPending" class="no-data-text">
              <td colspan="10">
                <p class="pt-3 pb-3 text-center font-weight-regular text-grey-darken-1">
                  No distributors match the current filters.
                </p>
              </td>
            </tr>
            <tr v-if="itemsPending && items.length === 0" class="no-data-text">
              <td colspan="10">
                <p class="pt-3 pb-3 text-center font-weight-regular text-grey-darken-1">
                  Loading...
                </p>
              </td>
            </tr>
          </tbody>
        </v-table>
        <table-pagination
          :page="pagination.page"
          :rows-per-page="pagination.rowsPerPage"
          :total-items="distributorCount"
          :rows-per-page-options="[25, 50, 100]"
          @update:page="onPageChange"
          @update:rows-per-page="onRowsPerPageChange"
        />
      </v-col>
    </v-row>
    <!--
      Disable confirmation. Only the DISABLE direction is gated: it locks every
      user of the tenant out and takes its storefronts offline. Re-enabling is
      not the dangerous direction, and gating it too would train an SA to click
      through the dialog that actually matters.
    -->
    <v-dialog
      v-if="confirmTarget"
      :model-value="true"
      max-width="480"
      persistent
    >
      <v-card data-testid="dl-disable-confirm">
        <v-card-title>Disable {{ confirmTarget.name }}?</v-card-title>
        <v-card-text>
          Nobody at <strong>{{ confirmTarget.name }}</strong> will be able to
          sign in, its storefronts will stop serving shoppers, and its billing
          will be suspended.
          <br /><br />
          This is reversible -- enabling the distributor again restores access
          and returns billing to exactly the state it is in now. Orders,
          stores, and campaigns are not changed either way.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            data-testid="dl-disable-confirm-no"
            @click="cancelAccessAction"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            data-testid="dl-disable-confirm-yes"
            @click="confirmAccessAction"
          >
            Disable distributor
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-alert
      v-if="accessError"
      type="error"
      variant="tonal"
      class="mt-3"
      data-testid="dl-disable-error"
    >
      {{ accessError }}
    </v-alert>
  </v-container>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import TablePagination from "@/components/TablePagination.vue";
import { useImpersonationStore } from "@/stores/impersonation";
import { setDistributorDisabled } from "@/services/distributorService";

const ORDER_BY_MAP = {
    name: "name",
    state: "state",
    stores: "stores",
    mrr: "mrr",
    sales_30d: "sales_30d",
    last_activity: "last_activity",
}

export default {
    components: {
        "table-pagination": TablePagination,
    },
    setup() {
        const impersonationStore = useImpersonationStore()
        return { impersonationStore }
    },
    data() {
        return {
            headers: [
                { text: "Distributor", value: "name" },
                { text: "Contact", value: "contact", sortable: false },
                { text: "State", value: "state" },
                { text: "Stores", value: "stores" },
                { text: "MRR", value: "mrr" },
                { text: "Sales 30D", value: "sales_30d" },
                { text: "Last activity", value: "last_activity" },
            ],
            stateOptions: [
                { text: "All states", value: "" },
                { text: "Active", value: "active" },
                { text: "Trialing", value: "trialing" },
                { text: "Past Due", value: "past_due" },
                { text: "Canceled", value: "canceled" },
                { text: "No plan", value: "none" },
            ],
            pagination: {
                page: 1,
                sortBy: "name",
                descending: false,
                rowsPerPage: 50,
            },
            items: [],
            distributorCount: 0,
            itemsPending: false,
            search: "",
            stateFilter: "",
            _searchTimer: null,
            // Enable/disable row action state. `confirmTarget` non-null means
            // the disable confirmation is open for that row; `pendingAccessId`
            // disables the button of the row currently being written so a
            // double-click cannot issue two POSTs.
            confirmTarget: null,
            pendingAccessId: null,
            accessError: "",
        }
    },
    mounted() {
        this.$store.state.title = "Distributor List"
        this.$store.state.topTitle = false
        this.loadDistributors()
    },
    beforeUnmount() {
        if (this._searchTimer) {
            clearTimeout(this._searchTimer)
        }
    },
    methods: {
        onPageChange(p) {
            this.pagination.page = p
            this.loadDistributors()
        },
        onRowsPerPageChange(val) {
            this.pagination.rowsPerPage = val
            this.pagination.page = 1
            this.loadDistributors()
        },
        sortColumn(header) {
            if (header.sortable === false) return
            if (this.pagination.sortBy === header.value) {
                this.pagination.descending = !this.pagination.descending
            } else {
                this.pagination.sortBy = header.value
                this.pagination.descending = false
            }
            this.loadDistributors()
        },
        getSortIcon(header) {
            if (header.value === this.pagination.sortBy) {
                return this.pagination.descending ? "arrow_downward" : "arrow_upward"
            }
            return "arrow_upward"
        },
        onSearchInput() {
            // 250ms debounce on search input. Cleared on each keystroke
            // so we only refetch once the user pauses.
            if (this._searchTimer) {
                clearTimeout(this._searchTimer)
            }
            this._searchTimer = setTimeout(() => {
                this.pagination.page = 1
                this.loadDistributors()
            }, 250)
        },
        async applyFilters() {
            this.pagination.page = 1
            await this.loadDistributors()
        },
        buildQuery() {
            const params = new URLSearchParams()
            if (this.search) params.set("q", this.search)
            if (this.stateFilter) params.set("state", this.stateFilter)
            const sortKey = ORDER_BY_MAP[this.pagination.sortBy] || "name"
            params.set("order_by", sortKey)
            params.set("direction", this.pagination.descending ? "desc" : "asc")
            params.set("limit", String(this.pagination.rowsPerPage))
            params.set(
                "offset",
                String((this.pagination.page - 1) * this.pagination.rowsPerPage),
            )
            return params.toString()
        },
        async loadDistributors() {
            this.itemsPending = true
            try {
                const query = this.buildQuery()
                const result = await this.$axios.get(
                    `/distributor/superadminList?${query}`,
                )
                this.items = result.data.distributors
                this.distributorCount = result.data.count
            } catch (err) {
                this.items = []
                this.distributorCount = 0
                // eslint-disable-next-line no-console
                console.error(describeError(err))
                this.$store.dispatch("raiseError", err)
            } finally {
                this.itemsPending = false
            }
        },
        formatState(status) {
            if (!status) return ""
            const map = {
                active: "Active",
                trialing: "Trialing",
                past_due: "Past Due",
                canceled: "Canceled",
            }
            return map[status] || status
        },
        formatDate(iso) {
            // Render YYYY-MM-DD only -- time of day adds noise. Mirrors the
            // retired redundant "full" SA list's formatDate. Null is
            // handled by the template's v-else em-dash, not here.
            if (!iso) return "—"
            return String(iso).slice(0, 10)
        },
        formatMrrCents(cents) {
            const c = Number(cents || 0)
            if (c === 0) return "$0"
            const dollars = c / 100
            if (dollars < 1000) return `$${dollars}`
            const k = dollars / 1000
            // Drop trailing .0 for whole-thousand values.
            const formatted = k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)
            return `$${formatted}k`
        },
        formatSalesCents(cents) {
            const c = Number(cents || 0)
            if (c === 0) return "$0"
            const dollars = c / 100
            if (dollars < 1000) return `$${dollars}`
            if (dollars < 1_000_000) {
                const k = dollars / 1000
                const formatted = k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)
                return `$${formatted}k`
            }
            const m = dollars / 1_000_000
            const formatted = m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)
            return `$${formatted}M`
        },
        // ---- Enable / disable (SA tenant access) --------------------------
        // Card: https://trello.com/c/qJidTXSJ
        //
        // This is entry point 1 of 2 for the same action -- the edit page
        // (pages/superadmin/DistributorEdit.vue) is the other. BOTH go through
        // `setDistributorDisabled` in the distributor service; neither POSTs
        // for itself. Two entry points, one write model.
        isDisabled(item) {
            if (!item) return false
            // `_disabledOverride` is set by a successful write in this session
            // and holds the SERVER's reported boolean. It takes precedence
            // over `disabled_at`, which is only refreshed by a refetch.
            //
            // Why a separate flag rather than writing `disabled_at`: the
            // endpoints return {id, changed, disabled} with NO timestamp, so
            // there is nothing truthful to put in a column named for a server
            // timestamp. Fabricating `new Date()` there would put a value the
            // server never produced into a field named after a server column
            // -- harmless while only its truthiness is read, and a real bug
            // the day someone renders it.
            if (typeof item._disabledOverride === "boolean") {
                return item._disabledOverride
            }
            return Boolean(item.disabled_at)
        },
        onAccessAction(item) {
            if (!item) return
            this.accessError = ""
            if (this.isDisabled(item)) {
                // Enabling restores access -- not the dangerous direction, so
                // it goes straight through without a confirmation.
                this.applyAccessChange(item, false)
                return
            }
            this.confirmTarget = item
        },
        cancelAccessAction() {
            this.confirmTarget = null
        },
        confirmAccessAction() {
            const target = this.confirmTarget
            this.confirmTarget = null
            if (target) {
                this.applyAccessChange(target, true)
            }
        },
        async applyAccessChange(item, disabled) {
            this.pendingAccessId = item.id
            this.accessError = ""
            try {
                const result = await setDistributorDisabled(item.id, disabled)
                // Render what the SERVER reports, never what we asked for.
                // The endpoints are idempotent, so `changed` can be false when
                // the tenant was already in the requested state -- and a
                // rejected write must not leave an optimistic lie on screen.
                const nowDisabled = Boolean(result && result.disabled)
                // Record the SERVER's reported state. Vue 3 reactivity picks
                // up the new property on this plain object because `items` is
                // itself reactive.
                item._disabledOverride = nowDisabled
            } catch (err) {
                const status = err && err.response && err.response.status
                if (status === 403 || status === 404) {
                    this.accessError =
                        "Permission denied. Only a Super Admin with tenant management rights can enable or disable a distributor."
                } else {
                    this.accessError = disabled
                        ? "Could not disable the distributor. Nothing was changed."
                        : "Could not enable the distributor. Nothing was changed."
                }
                // eslint-disable-next-line no-console
                console.error(describeError(err))
            } finally {
                this.pendingAccessId = null
            }
        },
        // ---- Impersonation (PR-R3, retained) -----------------------------
        // Impersonate directly from the row action -- no confirmation gate.
        async doImpersonate(target) {
            if (!target) return
            const id = target.id
            try {
                await this.impersonationStore.startImpersonation(id, target.name)
                if (typeof window !== "undefined" && window.location) {
                    window.location.reload()
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Impersonation failed:", describeError(err))
                this.$store.dispatch("raiseError", err)
            }
        },
    },
}
</script>

<style scoped>
/*
  Right-aligned action row above the list. `flex-end` rather than
  `space-between` because this row holds only the action -- there is no
  in-page title to push it away from (the SA Topbar owns the title). It
  stays right-aligned at every width, so no narrow-viewport special case is
  needed: with a single item there is nothing for it to collide with.
*/
.dl-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 16px;
}

.sa-filter-bar {
    flex-wrap: wrap;
}

.sa-health-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #cccccc;
}

.sa-health-dot--green {
    background: #2e7d32;
}

.sa-health-dot--yellow {
    background: #f9a825;
}

.sa-health-dot--red {
    background: #c62828;
}

.sa-health-dot--gray {
    background: #9e9e9e;
}

.sa-state-pill {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
}

.sa-state-pill--active {
    background: #e8f5e9;
    color: #2e7d32;
}

.sa-state-pill--trialing {
    background: #fff8e1;
    color: #f57f17;
}

.sa-state-pill--past_due {
    background: #ffebee;
    color: #c62828;
}

.sa-state-pill--canceled {
    background: #eeeeee;
    color: #424242;
}

.sa-disabled-badge {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: 10px;
    background: #ffebee;
    color: #c62828;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.dl-row-actions {
    white-space: nowrap;
}

.sa-empty {
    color: var(--tf-fg-4);
}

tr.no-data-text:hover {
    background: transparent !important;
}
</style>
